import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { TextGray } from "components/Atomos/Texts/TextGray";
import {
  EstiloConsultaPrevia,
  ParametroUrbanistico,
  UsoSolo
} from "store/modules/consultaprevia/types";
import { ParametrosUrbanisticos } from "./ParametrosUrbanisticos";
import { SwatchCamada } from "./SwatchCamada";
import { UsosSoloLista } from "./UsosSoloLista";

type Props = {
  sigla: string;
  nome: string;
  tipo?: string | null;
  descricao?: string | null;
  estilo?: EstiloConsultaPrevia | null;
  parametros: ParametroUrbanistico[];
  usosSolo: UsoSolo[];
  /** Selos da camada (categoria, vedações). */
  badges?: React.ReactNode;
  /** Ação de rodapé, p.ex. abrir a ficha completa da zona. */
  acao?: React.ReactNode;
};

/**
 * Uma camada incidente no ponto — zona urbana ou área especial. Cabeçalho
 * sempre visível; parâmetros e usos do solo ficam recolhidos porque um ponto
 * pode acumular várias áreas especiais e a lista aberta viraria um paredão.
 */
export const CartaoCamada = ({
  sigla,
  nome,
  tipo,
  descricao,
  estilo,
  parametros,
  usosSolo,
  badges,
  acao
}: Props) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      p="3"
      mb="3"
    >
      <Flex alignItems="center" gap="2">
        <SwatchCamada estilo={estilo} />
        <Box flex="1" minW={0}>
          <Text fontWeight="bold" fontSize="md" lineHeight="short">
            {sigla}
          </Text>
          <TextGray fontSize="sm" lineHeight="short">
            {nome}
          </TextGray>
        </Box>
      </Flex>

      {(tipo || badges) && (
        <Flex mt="2" gap="1" flexWrap="wrap" alignItems="center">
          {tipo && (
            <TextGray fontSize="xs" textTransform="uppercase" mr="1">
              {tipo}
            </TextGray>
          )}
          {badges}
        </Flex>
      )}

      {descricao && (
        <TextGray fontSize="xs" mt="2" whiteSpace="pre-line">
          {descricao}
        </TextGray>
      )}

      <Accordion allowMultiple mt="2">
        <AccordionItem border="none">
          <AccordionButton px="0" _hover={{ bgColor: "transparent" }}>
            <Box flex="1" textAlign="left">
              <Text fontSize="sm" fontWeight="medium">
                {`Parâmetros urbanísticos (${parametros?.length ?? 0})`}
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px="0" pt="0" pb="2">
            <ParametrosUrbanisticos parametros={parametros ?? []} />
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
          <AccordionButton px="0" _hover={{ bgColor: "transparent" }}>
            <Box flex="1" textAlign="left">
              <Text fontSize="sm" fontWeight="medium">
                {`Usos do solo (${usosSolo?.length ?? 0})`}
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px="0" pt="2" pb="2">
            <UsosSoloLista usosSolo={usosSolo ?? []} />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {acao && <Box mt="2">{acao}</Box>}
    </Box>
  );
};
